import { Check, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";

const GLP1PriceComparison = () => {
  return (
    <section className="py-16 md:py-24 bg-[#181818]">
      <div className="container mx-auto px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
              HillaryRx vs. TrumpRx
            </h2>
            <p className="text-xl md:text-2xl text-[#ffdb4d] font-semibold">
              GLP-1 Price Comparison
            </p>
          </div>

          {/* Comparison Tables */}
          <div className="space-y-8">
            {/* Starters Table */}
            <Card className="bg-white/5 border-white/10 backdrop-blur-sm overflow-hidden">
              <div className="bg-[#ffdb4d]/10 px-6 py-4 border-b border-white/10">
                <h3 className="text-xl font-bold text-[#ffdb4d]">
                  New to GLP-1s (Starters)
                </h3>
                <p className="text-white/70 text-sm mt-1">
                  HillaryRx: 3-month program, 50% off, escalating doses | TrumpRx: Lowest starting doses (monthly, cash-pay)
                </p>
              </div>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-white/80 font-semibold">Medication</TableHead>
                        <TableHead className="text-[#ffdb4d] font-semibold">HillaryRx (Compounded)</TableHead>
                        <TableHead className="text-red-500 font-semibold">TrumpRx (Brand-Name)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-white font-medium">
                          <div>
                            <span className="font-bold">Tirzepatide</span>
                            <p className="text-white/60 text-xs mt-1">GLP-1/GIP, like Zepbound</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-white">
                          <div className="space-y-1">
                            <p className="text-xs text-white/70">1 vial: 4 weekly doses each of 2.5mg, 5mg, 7.5mg</p>
                            <p className="text-2xl font-black text-[#ffdb4d]">$449</p>
                            <p className="text-sm text-[#ffdb4d]/80">($149/mo equiv.)</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-white">
                          <div className="space-y-1">
                            <p className="text-xs text-white/70">Zepbound</p>
                            <p className="text-2xl font-bold text-red-500">$299–$449/mo</p>
                            <p className="text-sm text-white/60">(lowest $299 for starter doses)</p>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-white font-medium">
                          <div>
                            <span className="font-bold">Semaglutide</span>
                            <p className="text-white/60 text-xs mt-1">GLP-1, like Ozempic/Wegovy</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-white">
                          <div className="space-y-1">
                            <p className="text-xs text-white/70">1 vial: 4 weekly doses each of 0.25mg, 0.5mg, 1mg</p>
                            <p className="text-2xl font-black text-[#ffdb4d]">$224</p>
                            <p className="text-sm text-[#ffdb4d]/80">($74/mo equiv.)</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-white">
                          <div className="space-y-1">
                            <p className="text-xs text-white/70">Ozempic / Wegovy</p>
                            <p className="text-2xl font-bold text-red-500">$149–$349/mo</p>
                            <p className="text-sm text-white/60">(lowest $149 for Wegovy Pill)</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Current Users Table */}
            <Card className="bg-white/5 border-white/10 backdrop-blur-sm overflow-hidden">
              <div className="bg-[#ffdb4d]/10 px-6 py-4 border-b border-white/10">
                <h3 className="text-xl font-bold text-[#ffdb4d]">
                  Current Users (Ongoing/Transfers)
                </h3>
                <p className="text-white/70 text-sm mt-1">
                  HillaryRx: 3-month program, 25% off, consistent doses (up to max) | TrumpRx: Typical ongoing doses (monthly, cash-pay)
                </p>
              </div>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-white/80 font-semibold">Medication</TableHead>
                        <TableHead className="text-[#ffdb4d] font-semibold">HillaryRx (Compounded)</TableHead>
                        <TableHead className="text-red-500 font-semibold">TrumpRx (Brand-Name)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-white font-medium">
                          <div>
                            <span className="font-bold">Tirzepatide</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-white">
                          <div className="space-y-1">
                            <p className="text-xs text-white/70">2.5mg–15mg available</p>
                            <p className="text-2xl font-black text-[#ffdb4d]">$670</p>
                            <p className="text-sm text-[#ffdb4d]/80">($223/mo equiv.)</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-white">
                          <div className="space-y-1">
                            <p className="text-xs text-white/70">Zepbound</p>
                            <p className="text-2xl font-bold text-red-500">$299–$449/mo</p>
                            <p className="text-sm text-white/60">(avg. $346)</p>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-white font-medium">
                          <div>
                            <span className="font-bold">Semaglutide</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-white">
                          <div className="space-y-1">
                            <p className="text-xs text-white/70">0.25mg–2.5mg available</p>
                            <p className="text-2xl font-black text-[#ffdb4d]">$335</p>
                            <p className="text-sm text-[#ffdb4d]/80">($111/mo equiv.)</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-white">
                          <div className="space-y-1">
                            <p className="text-xs text-white/70">Ozempic / Wegovy Pen</p>
                            <p className="text-2xl font-bold text-red-500">$199–$350/mo</p>
                            <p className="text-sm text-white/60">(avg. $350)</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Takeaway */}
          <Card className="mt-8 bg-[#ffdb4d]/10 border-[#ffdb4d]/30 backdrop-blur-sm">
            <CardContent className="p-6 md:p-8">
              <h3 className="text-xl font-bold text-[#ffdb4d] mb-4">Quick Takeaway</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#ffdb4d] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Starters</p>
                    <p className="text-white/70 text-sm">HillaryRx as low as <span className="text-[#ffdb4d] font-bold">$74/mo</span> vs. TrumpRx $149–$299/mo</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#ffdb4d] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Ongoing</p>
                    <p className="text-white/70 text-sm">HillaryRx <span className="text-[#ffdb4d] font-bold">$111–$223/mo</span> vs. TrumpRx $199–$449/mo</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#ffdb4d] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">All-In Pricing</p>
                    <p className="text-white/70 text-sm">HillaryRx includes consults, shipping & support</p>
                  </div>
                </div>
              </div>
              <p className="text-white/50 text-sm mt-4">
                TrumpRx = brand-name meds only (no bundled services)
              </p>
            </CardContent>
          </Card>

          {/* Safety Note */}
          <Alert className="mt-6 bg-white/5 border-[#ffdb4d]/30">
            <AlertTriangle className="h-4 w-4 text-[#ffdb4d]" />
            <AlertDescription className="text-white/70 text-sm">
              <span className="font-semibold text-white">Important Safety Note:</span> Compounded medications are not FDA-approved for safety, efficacy, or quality. Prescribed only when a provider determines they are appropriate. Consult your provider first. Results vary by individual. Side effects possible (e.g., nausea). These statements have not been evaluated by the FDA. Peptide protocols are compounded by licensed pharmacies and prescribed by licensed providers based on individual assessment. Not intended to diagnose, treat, cure, or prevent any disease. No ties to big pharma.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </section>
  );
};

export default GLP1PriceComparison;
